/**
 * **Réglages de rendu** de la vue de combat 3D (v0.10.1) — le GD trouvait la
 * vue trop pixélisée et les dés illisibles : plutôt que de deviner les bonnes
 * valeurs, le panneau Debug expose ces réglages et les applique **en direct**
 * (aucun rechargement), puis le GD nous renvoie le JSON.
 *
 * Module **pur** : aucun WebGL, aucun DOM (hors `localStorage` via
 * `src/ui/prefs.ts`, lui-même gardé par try/catch). Il porte le tableau des
 * plages, les valeurs par défaut et la normalisation d'un JSON venu de
 * l'extérieur.
 *
 * **v0.22.0 (A58)** : le pipeline pixel du lot 1 est retiré — le low-poly
 * (A39–A42) est l'unique rendu. Restent la définition, les ombres, le contour
 * des monstres, l'heure, le reflet de rareté, la caméra et la taille des dés
 * (et, depuis A65, la matière des dés ; depuis A67, leur ombrage) ;
 * `mode`, `pixelScale`, `atlasCell`, `outline`, `outlineThreshold`, `palette`,
 * `toonSteps`, `diceLight` et `faceEdges` disparaissent (migration `v: 6`).
 *
 * **v0.10.3** : le GD a tranché — il veut **exactement** le rendu de l'aperçu
 * qu'il a validé. Ce rendu est décrit une fois pour toutes par
 * {@link PRESET_APERCU}, qui **est** le défaut (et le bouton « Aperçu
 * low-poly » du panneau Debug). Les curseurs restent : c'est le point de
 * départ, pas une prison.
 *
 * Testé dans `tests/game3d/renderParams.test.ts`.
 */
import { readJsonPref, writeJsonPref } from '../ui/prefs';
import type { CycleHour } from './scenes/cycle';

/**
 * **Définition** du rendu : le `devicePixelRatio` est plafonné à 2 (`x2`,
 * défaut) ou à 1,5 (`x1_5`), ou pris tel quel (`native`, plafonné à 3 par
 * sécurité).
 */
export type RenderResolution = 'native' | 'x2' | 'x1_5';
/**
 * **Heure** de la scène low-poly. `auto` (défaut depuis la v0.21.0) : jour
 * ou nuit tiré au sort à chaque combat, constant pendant le combat (v0.27.1,
 * A73 ; de la v0.21.0 à la v0.27.0, A57 : cycle continu du matin à la nuit
 * au fil du chrono) — `scenes/cycle.ts`. Les quatre repères la **figent**
 * (comparaisons, captures). Avant la v0.21.0 : crépuscule par défaut, et jour.
 */
export type TimeOfDay = 'auto' | CycleHour;
/**
 * **Matière des dés** (A65) : `glossy`, le plastique brillant de la planche
 * « Coins arrondis » (V1) ; `matte`, l'ombrage du décor et le reflet de
 * rareté de la v0.24 (liseré porté par l'arrondi). Défaut `glossy` en
 * v0.25.0 ; `matte` depuis A67 (dés low-poly : la matière mate facettée,
 * comme le décor et les monstres), la brillante restant là pour comparer.
 */
export type DiceMaterial = 'glossy' | 'matte';
/**
 * **Ombrage des dés** (A67) : `faceted` (défaut), normales **plates** par
 * facette sur l'arrondi — chaque bande et chaque triangle de calotte se voit,
 * comme le décor et les monstres low-poly — ; `smooth`, les normales lissées
 * de la v0.25.0 (A65). Le nombre de bandes de l'arrondi en dépend
 * (`diceLowpoly.DIE_ROUND_BANDS`).
 */
export type DiceShading = 'faceted' | 'smooth';
/** Réglages de rendu de la vue 3D. Tous appliqués sans rechargement. */
export interface RenderParams {
  /** Définition du rendu (plafond du `devicePixelRatio`). */
  resolution: RenderResolution;
  /** Épaisseur du contour des monstres, en pixels écran (0 = aucun). */
  monsterOutline: number;
  /** Heure de la scène : `auto` suit le combat, les autres figent. */
  timeOfDay: TimeOfDay;
  /** Échelle monde d'un dé Maître (1 = arête d'une unité pour un d6). */
  masterDieScale: number;
  /** Part de la taille des Maîtres reprise par les dés Basiques. */
  basicDieRatio: number;
  /** Multiplicateur de la distance œil ↔ point visé (zoom sur le plateau). */
  cameraDistance: number;
  /** Multiplicateur de la hauteur de l'œil (vue plus ou moins plongeante). */
  cameraHeight: number;
  /** Multiplicateur de la focale (téléobjectif ↔ grand angle). */
  cameraFocal: number;
  /** Cartes d'ombre portée (décor, dés, sac, monstres). */
  shadows: boolean;
  /**
   * **Reflet de rareté** (v0.13.0, A45–A46, low-poly) : la rareté d'un dé se lit
   * sur le dé (reflet et liseré teintés, Prismatique irisé) et l'anneau au sol
   * disparaît. `false` : dés os nus et anneau au sol d'avant, pour comparer.
   * Champ ajouté sans migration : absent d'une préférence, il prend son défaut.
   */
  raritySheen: boolean;
  /**
   * **Particules magiques des dés** (v0.35.0, A78) : nuage de grains de la
   * couleur de rareté autour des dés Maîtres Inhabituels et plus. `false` :
   * aucune passe dessinée (case « Particules magiques des dés » des options
   * de l'Accalmie, ligne Debug). Champ ajouté sans migration : absent d'une
   * préférence, il prend son défaut (`true`).
   */
  diceParticles: boolean;
  /**
   * **Matière des dés** (A65) : plastique brillant ou mate. Champ ajouté sans
   * migration : absent d'une préférence, il prend son défaut (`matte` depuis
   * A67, `glossy` en v0.25.0).
   */
  diceMaterial: DiceMaterial;
  /**
   * **Ombrage des dés** (A67) : facetté ou lissé. Champ ajouté sans
   * migration : absent d'une préférence, il prend son défaut (`faceted`).
   */
  diceShading: DiceShading;
}

/**
 * **Préréglage « Aperçu low-poly »** (v0.11.0) : le rendu des aperçus validés
 * par le GD (`diceordie-da-preview.html` mode DA2, `diceordie-lapin-lowpoly.html`)
 * — pleine définition plafonnée à ×2, ombres portées, contour des monstres de
 * 2 px, dés à l'échelle **1** (la taille « ×1 » des aperçus, où le d6 fait
 * une unité d'arête). L'heure, figée au crépuscule jusqu'à la v0.20.x, suit le
 * combat depuis la v0.21.0 (`auto`, A57 : le GD veut le cycle par défaut).
 */
export const PRESET_APERCU: Readonly<RenderParams> = {
  resolution: 'x2',
  monsterOutline: 2,
  timeOfDay: 'auto',
  masterDieScale: 1,
  basicDieRatio: 0.6,
  cameraDistance: 1,
  cameraHeight: 1,
  cameraFocal: 1,
  shadows: true,
  raritySheen: true,
  // A78 : particules des dés Maîtres, désactivables (téléphone lent).
  diceParticles: true,
  // A67 : dés low-poly par défaut — matière mate, arrondi facetté.
  diceMaterial: 'matte',
  diceShading: 'faceted',
};

/**
 * Valeurs livrées en **v0.11.0** : c'est la référence de « Réinitialiser », et
 * c'est le préréglage « Aperçu low-poly ».
 */
export const DEFAULT_RENDER_PARAMS: Readonly<RenderParams> = PRESET_APERCU;

/** Clés continues (curseurs) — les autres sont des choix discrets. */
export type RenderSliderKey =
  | 'masterDieScale'
  | 'basicDieRatio'
  | 'cameraDistance'
  | 'cameraHeight'
  | 'cameraFocal'
  | 'monsterOutline';

/** Description d'un curseur : plage, pas et libellé du panneau Debug. */
export interface RenderSlider {
  readonly key: RenderSliderKey;
  readonly label: string;
  readonly min: number;
  readonly max: number;
  readonly step: number;
}

/**
 * Le tableau des plages, **source unique** : le panneau Debug construit ses
 * curseurs avec, et `normalizeRenderParams` borne avec.
 */
export const RENDER_SLIDERS: readonly RenderSlider[] = [
  { key: 'monsterOutline', label: 'Contour des monstres (px)', min: 0, max: 4, step: 0.5 },
  { key: 'masterDieScale', label: 'Taille des dés Maîtres', min: 0.6, max: 2, step: 0.05 },
  { key: 'basicDieRatio', label: 'Taille des Basiques (ratio)', min: 0.3, max: 1, step: 0.05 },
  { key: 'cameraDistance', label: 'Distance caméra', min: 0.6, max: 1.5, step: 0.05 },
  { key: 'cameraHeight', label: 'Hauteur caméra', min: 0.6, max: 1.5, step: 0.05 },
  { key: 'cameraFocal', label: 'Focale', min: 0.7, max: 1.4, step: 0.05 },
];

const SLIDER_BY_KEY: Readonly<Record<RenderSliderKey, RenderSlider>> = Object.fromEntries(
  RENDER_SLIDERS.map((s) => [s.key, s]),
) as Record<RenderSliderKey, RenderSlider>;

/** Valeurs admises des réglages discrets (le panneau en fait des boutons). */
export const RESOLUTION_CHOICES: readonly RenderResolution[] = ['native', 'x2', 'x1_5'];
export const TIME_OF_DAY_CHOICES: readonly TimeOfDay[] = ['auto', 'morning', 'day', 'dusk', 'night'];
export const DICE_MATERIAL_CHOICES: readonly DiceMaterial[] = ['glossy', 'matte'];
export const DICE_SHADING_CHOICES: readonly DiceShading[] = ['faceted', 'smooth'];

/** Plafond du `devicePixelRatio` pour chaque définition. */
export const RESOLUTION_DPR_CAP: Readonly<Record<RenderResolution, number>> = {
  native: 3,
  x2: 2,
  x1_5: 1.5,
};

/**
 * Rapport pixels du tampon / pixels CSS pour une définition et un
 * `devicePixelRatio` donnés. Jamais sous 1 : un écran à 1 garde sa définition.
 */
export function effectivePixelRatio(resolution: RenderResolution, dpr: number): number {
  const d = Number.isFinite(dpr) && dpr > 0 ? dpr : 1;
  return Math.max(1, Math.min(RESOLUTION_DPR_CAP[resolution] ?? 2, d));
}

/** Préférence JSON des réglages (écrite à chaque changement). */
export const RENDER_PARAMS_PREF_KEY = 'diceordie.view3d.params';
/**
 * Ancienne préférence v0.10.0 (×2/×3/×4). Elle n'est **plus** reprise depuis la
 * v0.10.3 : la migration remplace tout réglage d'avant le préréglage
 * « Aperçu ». La clé reste nommée ici — c'est elle que le panneau Debug efface.
 */
export const LEGACY_PIXEL_PREF_KEY = 'diceordie.view3d.pixel';

/** Arrondi à 3 décimales : évite le bruit flottant dans le JSON exporté. */
function round3(v: number): number {
  return Math.round(v * 1000) / 1000;
}

/** Borne et cale une valeur continue sur le pas de son curseur. */
function quantize(raw: unknown, slider: RenderSlider, fallback: number): number {
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (raw === null || raw === undefined || raw === '' || !Number.isFinite(n)) return fallback;
  const clamped = Math.min(slider.max, Math.max(slider.min, n));
  const steps = Math.round((clamped - slider.min) / slider.step);
  return round3(Math.min(slider.max, slider.min + steps * slider.step));
}

function pickString<T extends string>(raw: unknown, choices: readonly T[], fallback: T): T {
  return typeof raw === 'string' && (choices as readonly string[]).includes(raw) ? (raw as T) : fallback;
}

function pickBool(raw: unknown, fallback: boolean): boolean {
  if (typeof raw === 'boolean') return raw;
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return fallback;
}

/**
 * Normalise un objet quelconque (JSON collé par le GD, préférence d'une
 * version antérieure, patch partiel) en `RenderParams` **valide** : chaque clé
 * absente ou aberrante retombe sur la valeur par défaut.
 */
export function normalizeRenderParams(raw: unknown): RenderParams {
  const src = (raw && typeof raw === 'object' ? raw : {}) as Partial<Record<keyof RenderParams, unknown>>;
  const d = DEFAULT_RENDER_PARAMS;
  const slider = (key: RenderSliderKey): number => quantize(src[key], SLIDER_BY_KEY[key], d[key]);
  // Les clés inconnues — dont celles du mode pixel retiré en v0.22.0 et la clé
  // `bestiary` retirée en v0.34.1 (A71) — sont simplement ignorées : seul ce qui suit est relu.
  return {
    resolution: pickString(src.resolution, RESOLUTION_CHOICES, d.resolution),
    monsterOutline: slider('monsterOutline'),
    timeOfDay: pickString(src.timeOfDay, TIME_OF_DAY_CHOICES, d.timeOfDay),
    masterDieScale: slider('masterDieScale'),
    basicDieRatio: slider('basicDieRatio'),
    cameraDistance: slider('cameraDistance'),
    cameraHeight: slider('cameraHeight'),
    cameraFocal: slider('cameraFocal'),
    shadows: pickBool(src.shadows, d.shadows),
    raritySheen: pickBool(src.raritySheen, d.raritySheen),
    diceParticles: pickBool(src.diceParticles, d.diceParticles),
    diceMaterial: pickString(src.diceMaterial, DICE_MATERIAL_CHOICES, d.diceMaterial),
    diceShading: pickString(src.diceShading, DICE_SHADING_CHOICES, d.diceShading),
  };
}

/** Vrai si les deux jeux de réglages sont identiques champ à champ. */
export function sameRenderParams(a: RenderParams, b: RenderParams): boolean {
  for (const key of Object.keys(DEFAULT_RENDER_PARAMS) as Array<keyof RenderParams>) {
    if (a[key] !== b[key]) return false;
  }
  return true;
}

/**
 * **Version du JSON de préférence** (v0.22.0 : 6).
 *
 *  - `v: 6` : relu tel quel ;
 *  - `v: 5` (v0.21.x) : tout ce qui existe encore est gardé ; les clés du mode
 *    pixel (`mode`, `pixelScale`, `atlasCell`, `outline`, `outlineThreshold`,
 *    `palette`, `toonSteps`, `diceLight`, `faceEdges`) sont **ignorées** et
 *    disparaissent du JSON réécrit (A58) ;
 *  - `v: 4` (v0.11.0 à v0.20.x) : idem, et l'heure passe à `auto` — elle ne
 *    pouvait valoir que `dusk` (défaut) ou `day`, et le GD veut le cycle par
 *    défaut (A57) ;
 *  - `v: 3` (v0.10.3) : seules la caméra, la taille des Basiques et les
 *    ombres sont gardées ; le reste prend les défauts (dés à la taille de
 *    l'aperçu low-poly) ;
 *  - sans `v` ou plus ancien : remplacé par les défauts.
 *
 * Dans tous les cas le JSON est réécrit aussitôt avec la version courante :
 * la migration n'a lieu **qu'une fois**.
 */
export const RENDER_PARAMS_VERSION = 6;

/** Ce qui est réellement écrit dans `localStorage` : les réglages + `v`. */
export interface StoredRenderParams extends RenderParams {
  readonly v: number;
}

/** Réglages persistés (avec la migration décrite à {@link RENDER_PARAMS_VERSION}). */
export function readRenderParams(): RenderParams {
  const stored = readJsonPref<Record<string, unknown>>(RENDER_PARAMS_PREF_KEY);
  const raw: Record<string, unknown> = stored && typeof stored === 'object' ? { ...stored } : {};
  const version = Number(raw.v);
  if (version === RENDER_PARAMS_VERSION) return normalizeRenderParams(raw);
  let fresh: RenderParams;
  if (version === 5) {
    fresh = normalizeRenderParams(raw);
  } else if (version === 4) {
    fresh = normalizeRenderParams({ ...raw, timeOfDay: 'auto' });
  } else if (version === 3) {
    const kept: Record<string, unknown> = {};
    for (const key of ['basicDieRatio', 'cameraDistance', 'cameraHeight', 'cameraFocal', 'shadows']) {
      kept[key] = raw[key];
    }
    fresh = normalizeRenderParams(kept);
  } else {
    fresh = { ...DEFAULT_RENDER_PARAMS };
  }
  writeRenderParams(fresh);
  return fresh;
}

export function writeRenderParams(params: RenderParams): void {
  const stored: StoredRenderParams = { ...params, v: RENDER_PARAMS_VERSION };
  writeJsonPref(RENDER_PARAMS_PREF_KEY, stored);
}
